import { differenceInMinutes, isBefore, isSameDay, parseISO } from 'date-fns';
import { Suspense, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { MemoryRouter as Router } from 'react-router';

import { request } from '!core/api';
import {
  i18nSlice,
  selectCurrentLanguageCode,
  selectCurrentLanguageId,
} from '!core/features';
import {
  dbConfigurationsSlice,
  selectConfigurationParameterById,
} from '!core/redux';
import { expressions } from '!i18n';
import { TrlFeedbackModal } from '»components';
import {
  isAuthenticatedSelector,
  logoutSlice,
  onboardingSlice,
} from '»components/Auth';
import {
  invalidTokenSelector,
  isLogoutAlertOpen,
} from '»components/Auth/Logout/logout.selectors';
import { legalStatusListSelector } from '»components/Auth/Onboarding/redux/onboarding.selectors';
import { RecoverPassword } from '»components/Auth/RecoverPassword';
import { menusSlice } from '»components/Menus';
import { AppBar } from '»components/templates';
import { isStinvestActiveClient } from '»utils/clientsUtils';
import {
  AppBarContextProvider,
  NegModalContextProvider,
} from '»utils/contexts';
import { useCurrentPage, useUserAuthentication } from '»utils/customHooks';
import { NavigateSetter } from '»utils/navigation';
import { getDateTimeFromTimeZone } from '»utils/time';

import { AppWrapperStyled } from './App.styles';
import { AppBody } from './AppBody';

export const App = () => {
  const dispatch = useDispatch();

  const storedLanguageId = useSelector(selectCurrentLanguageId);
  const storedLanguageCode = useSelector(selectCurrentLanguageCode);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const languageId = useSelector(selectCurrentLanguageId);
  const languageCode = useSelector(selectCurrentLanguageCode);
  const showLogoutAlert = useSelector(isLogoutAlertOpen);
  const showInvalidTokenMsg = useSelector(invalidTokenSelector);
  const legalStatusList = useSelector(legalStatusListSelector);
  const hasOnboarding =
    useSelector((state) =>
      selectConfigurationParameterById(state, 'ONBOARDING'),
    )?.value === 'Y';

  // eslint-disable-next-line no-undef
  const myConfig = config();
  const { underMaintenanceDateTimePT } = myConfig;

  const currentTimePT = getDateTimeFromTimeZone({
    timeZone: 'Europe/Lisbon',
  });
  const maintenanceTime = parseISO(underMaintenanceDateTimePT);

  const isThirtyMinutesUntilMaintenance = () =>
    isSameDay(currentTimePT, maintenanceTime) &&
    differenceInMinutes(maintenanceTime, currentTimePT) <= 30;

  const [showInMaintenanceAlert, setShowInMaintenanceAlert] = useState(
    isThirtyMinutesUntilMaintenance(),
  );

  const { isUserAuthenticated } = useUserAuthentication();

  const { isProofPreviewPage } = useCurrentPage();

  const getI18nAvailable = i18nSlice.actions.culturesRequest;
  const searchParams = new URLSearchParams(document.location.search);
  const urlToken = searchParams.get('token');
  const urlUserId = searchParams.get('user');
  const isWelcome = urlToken !== null && urlUserId !== null;

  useEffect(() => {
    dispatch(
      dbConfigurationsSlice.actions.dbConfigurationsRequest({
        detailIds: [
          'OTP_SEND_INTERVAL',
          'INACTIVITY_TIME',
          'TIME_ZONE',
          'ONBOARDING',
          'ONBOARDING_OTP_START',
          'ONBOARDING_OTP_END',
          'ONBOARDING_OTP_DELIVERY_METHODS_END',
          'ONBOARDING_CONTRACT_PREVIEW',
          'ONBOARDING_DATA_ACCEPT',
          'ONBOARDING_VIDEO_LINK',
          'QUESTIONNAIRE',
          'ENFORCE_OTP_LOGIN',
          'ENFORCE_OTP_CHANGE_PASSWORD',
          'ENFORCE_OTP_RECOVER_PASSWORD',
          'ENFORCE_OTP_STOCK_ORDER',
          'ENFORCE_OTP_PRIMARY_ORDER',
          'ENFORCE_OTP_FUNDS_PRIMARY',
          'ENFORCE_OTP_FUNDS_MANAGER',
        ],
        parameterId: 'CR_CHNN_NOAUTH',
        noAuth: true,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function getLegalStatusList() {
      if (
        isStinvestActiveClient &&
        hasOnboarding &&
        languageId &&
        languageCode &&
        legalStatusList.length === 0
      )
        dispatch(onboardingSlice.actions.getLegalStatusListRequest());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasOnboarding, languageId, languageCode, legalStatusList.length],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(getI18nAvailable());
    } else {
      request.setCulture({
        languageId: storedLanguageId,
        languageCode: storedLanguageCode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getI18nAvailable]);

  const browserUrl = window.location.pathname.toLowerCase();
  const shouldOpenLoginFromUrl = browserUrl.includes('/login');
  const initialRoute = shouldOpenLoginFromUrl ? '/Login' : '/';

  useEffect(() => {
    if (shouldOpenLoginFromUrl)
      dispatch(
        menusSlice.actions.setActiveMenuItem({
          activeMenuItem: 'LOGIN',
        }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseLogoutAlert = () => {
    dispatch(logoutSlice.actions.hideLogoutAlert());
  };

  const handleCloseInMaintenanceAlert = () => {
    setShowInMaintenanceAlert(false);
  };

  const timeUntilMaintenance = () => {
    if (isBefore(maintenanceTime, currentTimePT)) return 0;

    const minutes = differenceInMinutes(maintenanceTime, currentTimePT) % 60;

    return minutes || 1;
  };

  return (
    <Suspense fallback={<div />}>
      {languageId && (
        <IntlProvider
          defaultLocale={languageCode}
          locale={languageCode}
          key={languageId}
          messages={expressions[languageId]}
        >
          <TrlFeedbackModal
            title={{ content: 'TRLT_UI-MAINTENANCE_WARNING' }}
            descr={{
              content: 'TRLT_UI-MAINTENANCE_WARNING_DESCR',
              values: { minutes: timeUntilMaintenance() },
            }}
            type="warning"
            open={showInMaintenanceAlert}
            onClose={handleCloseInMaintenanceAlert}
          />
          {isWelcome ? (
            <RecoverPassword urlToken={urlToken} />
          ) : (
            <>
              {!isUserAuthenticated && (
                <TrlFeedbackModal
                  title={{ content: 'TRLT_UI-SESSION_FINISHED' }}
                  descr={{
                    content: showInvalidTokenMsg
                      ? 'TRLT_UI-SESSION_EXP_MSG'
                      : 'TRLT_UI-SESSION_FINISHED_MSG',
                  }}
                  type="warning"
                  open={showLogoutAlert}
                  onClose={handleCloseLogoutAlert}
                />
              )}
              <AppWrapperStyled
                hasMarginAndPadding={!isProofPreviewPage}
                isUserSuccessfullyAuthenticated={isUserAuthenticated}
              >
                <Router initialEntries={[initialRoute]}>
                  <NavigateSetter />
                  <AppBarContextProvider>
                    <>
                      <NegModalContextProvider>
                        <AppBar />
                      </NegModalContextProvider>
                      <NegModalContextProvider>
                        <AppBody />
                      </NegModalContextProvider>
                    </>
                  </AppBarContextProvider>
                </Router>
              </AppWrapperStyled>
            </>
          )}
        </IntlProvider>
      )}
    </Suspense>
  );
};
